import React from 'react';
import { Box, Paper, Typography, Tooltip, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import IntervalBar from './IntervalBar';
import {
  DESIGNS, VERDICT_COLOR, readHeadline, headlineSentence, hasPooled, compositionText, fmtPts, fmtPct, fmtInt, fmtInterval,
} from '../../utils/influenceScorecard';

function HeadlineCard({ design, headline, horizon, secondary = false }) {
  const meta = DESIGNS[design];
  const r = readHeadline(headline, design, horizon);
  const color = VERDICT_COLOR[r?.verdict || 'insufficient'];
  return (
    <Paper elevation={secondary ? 0 : 3} variant={secondary ? 'outlined' : 'elevation'} aria-label={`${meta.title} card`} sx={{ p: secondary ? 2 : 2.5, flex: secondary ? '1 1 280px' : '2 1 400px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
          {secondary ? `Secondary check: ${meta.title}` : meta.title}
        </Typography>
        <Tooltip title={meta.question} arrow placement="top"><InfoOutlinedIcon fontSize="small" color="action" /></Tooltip>
      </Box>
      {!r ? (
        <Typography variant="body2" color="text.secondary">No result for this horizon.</Typography>
      ) : (
        <>
          <Typography variant={secondary ? 'h4' : 'h3'} component="p" sx={{ fontWeight: 700, color }} aria-label={`${meta.short} lift`}>
            {r.lift == null ? 'n/a' : fmtPts(r.lift)}
          </Typography>
          <IntervalBar lift={r.lift} lo={r.lo} hi={r.hi} verdict={r.verdict} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            95% interval {fmtInterval(r.lo, r.hi)} · n = {fmtInt(r.nClosed)} closed deals · {fmtPct(r.openSharePct)} still open
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Win rate within {r.horizon} days: {fmtPct(r.treatedRate, 1)} for these deals vs {fmtPct(r.baselineStdRate, 1)} for comparable deals with no attending SE
            (same age and size mix; {fmtPct(r.baselineOpenSharePct)} of those still open).
          </Typography>
          {design === 'pooled' && r.composition && (
            <Typography variant="body2" aria-label="Pooled composition" sx={{ fontWeight: 600 }}>{compositionText(r.composition)}</Typography>
          )}
          <Typography variant="caption" color="text.secondary">{headlineSentence(r, design, { withTail: false })}</Typography>
          {design === 'pooled' && (
            <Typography variant="caption" color="text.secondary" aria-label="Pooled caveat">
              This number is mostly deals created after the first attendance. It is an association, not proof that attending caused the difference.
            </Typography>
          )}
          {secondary && (
            <Typography variant="caption" color="text.secondary">
              Deals already open when an SE first attended are a small share of influenced deals; this is the cleaner before/after comparison and is shown as a check on the headline.
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
}

function DetailsTable({ headline, horizon }) {
  const rows = ['pooled', 'landmark', 'created_after'].map((d) => ({ d, r: readHeadline(headline, d, horizon) })).filter((x) => x.r);
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label="Headline detail by group">
        <TableHead>
          <TableRow>
            <TableCell>Group ({horizon}-day horizon)</TableCell>
            <TableCell align="right">Win-rate difference</TableCell>
            <TableCell align="right">95% interval</TableCell>
            <TableCell align="right">Closed deals</TableCell>
            <TableCell align="right">Still open</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ d, r }) => (
            <TableRow key={d}>
              <TableCell>{d === 'pooled' ? 'All influenced deals (headline)' : d === 'landmark' ? 'Open at first attendance (secondary check)' : 'Created after first attendance (detail)'}</TableCell>
              <TableCell align="right">{r.lift == null ? 'not enough data' : fmtPts(r.lift)}</TableCell>
              <TableCell align="right">{fmtInterval(r.lo, r.hi)}</TableCell>
              <TableCell align="right">{fmtInt(r.nClosed)}</TableCell>
              <TableCell align="right">{fmtPct(r.openSharePct)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export default function InfluenceHeadlineCards({ headline, horizon = 180, showDetails = true }) {
  if (hasPooled(headline)) {
    return (
      <Box aria-label="Influence headline numbers">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'stretch' }}>
          <HeadlineCard design="pooled" headline={headline} horizon={horizon} />
          <HeadlineCard design="landmark" headline={headline} horizon={horizon} secondary />
        </Box>
        {showDetails && (
          <Box sx={{ mt: 1.5 }}>
            <DetailsTable headline={headline} horizon={horizon} />
          </Box>
        )}
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }} aria-label="Influence headline numbers">
      <HeadlineCard design="landmark" headline={headline} horizon={horizon} />
      <HeadlineCard design="created_after" headline={headline} horizon={horizon} />
    </Box>
  );
}
